import { LayoutTop, NotificationText, PackageCheck, PackagePlus, PlayCircle, User01, ArrowUpRight, Plus, X } from '@untitled-ui/icons-react/build/cjs';
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import { ROUTES } from 'src/constants/routes';
import { WorkflowSteps, setCurrentUser, setWorkflowSteps } from 'src/redux/auth.slice';
import { AppDispatch, RootState } from 'src/redux/store';
import { INotificationTypes, saveNotificationSettings } from 'src/services/CustomerService';
import WalToast from 'src/utils/WalToast';

import checkMarkIcon from '../../assets/img/checkmarkicon.svg';
import gettingStartedBook from '../../assets/img/getting-started-book.svg';
import gettingStartedInvite from '../../assets/img/getting-started-invite.svg';
import gettingStartedNotifications from '../../assets/img/getting-started-notifications.svg';
import gettingStartedQuote from '../../assets/img/getting-started-quote.svg';
import gettingStartedWatch from '../../assets/img/getting-started-watch.svg';

import NotificationSettingsModal from './NotificationSettingsModal';
import WatchVideoModal from './UpcomingPickupDelivery/WatchVideoModal';

// Define step types
type StepId = 'watch' | 'invite' | 'quote' | 'book' | 'notifications';

interface Step {
    id: StepId;
    contentTitle: string;
    menuTitle: string;
    description: string;
    icon: React.ReactNode;
    status: 'done' | 'active' | 'pending';
    videoUrl?: string;
    videoTitle?: string;
    thumbnailGradient: string;
    primaryAction?: {
        label: string;
        onClick: () => void;
    };
    secondaryAction?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
}

interface StepContentProps {
    step: Step;
    onWatch: () => void;
    onNext?: () => void;
    isLastStep?: boolean;
    isNotificationsEnabled?: boolean;
    isDisabling?: boolean;
    onDisableNotifications?: () => void;
}

// Illustrations are >10KB so CRA emits them as separate files instead of inlining — preloaded on mount (see PLACEHOLDER_IMAGES) so switching steps doesn't flash blank while the browser fetches them.
const PLACEHOLDER_IMAGES = [gettingStartedWatch, gettingStartedInvite, gettingStartedQuote, gettingStartedBook, gettingStartedNotifications];

// No video available yet for this step — shows a static placeholder illustration instead of the "How to" watch card.
const PlaceholderVideoCard: React.FC<{ src: string }> = ({ src }) => (
    <div className="rounded-xl bg-[#EEF4FF] px-4 py-4 max-w-[311px] h-[176px] mx-auto relative">
        <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover rounded-xl" />
    </div>
);

const WatchStepContent: React.FC<StepContentProps & { t: (key: string) => string }> = ({ step, onWatch, onNext, t }) => (
    <>
        <div className="w-[50%]  xlm:w-[40%] mr-auto flex flex-row self-center">
            <div className="border rounded-[8px] border-utilityGray200 w-[40px] h-[40px] flex items-center justify-center shadow-xs mr-4 min-w-[40px] min-h-[40px]">
                <LayoutTop className="w-[24px] h-[24px] text-textSecondary" />
            </div>
            <div className="flex flex-col">
                <div className="flex flex-col">
                    <h6 className="text-grayLight900 text-[16px] leading-[24px] font-semibold mb-1 text-nowrap">{step.contentTitle}</h6>
                    <p className="text-textSecondary text-[12px] leading-[18px] font-normal max-w-[300px] line-clamp-2">{step.description}</p>
                </div>
                <div className="pt-4 flex gap-x-3">
                    <ButtonCmp className="btn-outline-primary" icon={<PlayCircle className="w-4 h-4" />} onClick={onWatch}>
                        {step.primaryAction?.label}
                    </ButtonCmp>
                    {onNext && (
                        <ButtonCmp className="btn_secondary_black" onClick={onNext}>
                            {t('Next')}
                        </ButtonCmp>
                    )}
                </div>
            </div>
        </div>
        <hr className="bg-utilityGray200 h-[70%] w-[1.5px] self-center" />
        <div className="w-[46%] ml-auto self-center justify-end">
            <PlaceholderVideoCard src={gettingStartedWatch} />
        </div>
    </>
);

const InviteStepContent: React.FC<StepContentProps & { t: (key: string) => string }> = ({ step, onNext, t }) => (
    <>
        <div className="w-[50%] xlm:w-[40%] mr-auto flex flex-row self-center">
            <div className="border rounded-[8px] border-utilityGray200 w-[40px] h-[40px] flex items-center justify-center shadow-xs mr-4 min-w-[40px] min-h-[40px]">
                <User01 className="w-5 h-5 text-textSecondary min-w-[20px] min-h-[20px]" />
            </div>
            <div className="flex flex-col">
                <div className="flex flex-col">
                    <h6 className="text-grayLight900 text-[16px] leading-[24px] font-semibold mb-1 text-nowrap">{step.contentTitle}</h6>
                    <p className="text-textSecondary text-[12px] leading-[18px] font-normal max-w-[300px] line-clamp-2">{step.description}</p>
                </div>
                <div className="pt-4 flex gap-x-3">
                    <ButtonCmp className="btn-outline-primary" icon={<ArrowUpRight className="w-4 h-4" />} onClick={step.primaryAction?.onClick}>
                        {step.primaryAction?.label}
                    </ButtonCmp>
                    {onNext && (
                        <ButtonCmp className="btn_secondary_black" onClick={onNext}>
                            {t('Next')}
                        </ButtonCmp>
                    )}
                </div>
            </div>
        </div>
        <hr className="bg-utilityGray200 h-[70%] w-[1.5px] self-center" />
        <div className="w-[46%] ml-auto self-center justify-end">
            <PlaceholderVideoCard src={gettingStartedInvite} />
        </div>
    </>
);

const QuoteStepContent: React.FC<StepContentProps & { t: (key: string) => string }> = ({ step, onNext, t }) => (
    <>
        <div className="w-[50%] xlm:w-[40%] mr-auto self-center flex flex-row">
            <div className="border rounded-[8px] border-utilityGray200 w-[40px] h-[40px] flex items-center justify-center shadow-xs mr-4 min-w-[40px] min-h-[40px]">
                <PackagePlus className="w-5 h-5 text-textSecondary min-w-[20px] min-h-[20px]" />
            </div>
            <div className="flex flex-col">
                <div className="flex flex-col">
                    <h6 className="text-grayLight900 text-[16px] leading-[24px] font-semibold mb-1 text-nowrap">{step.contentTitle}</h6>
                    <p className="text-textSecondary text-[12px] leading-[18px] font-normal max-w-[300px] line-clamp-2">{step.description}</p>
                </div>
                <div className="pt-4 flex gap-x-3">
                    <ButtonCmp className="btn-outline-primary" icon={<Plus className="w-4 h-4" />} onClick={step.primaryAction?.onClick}>
                        {step.primaryAction?.label}
                    </ButtonCmp>
                    {onNext && (
                        <ButtonCmp className="btn_secondary_black" onClick={onNext}>
                            {t('Next')}
                        </ButtonCmp>
                    )}
                </div>
            </div>
        </div>
        <hr className="bg-utilityGray200 h-[70%] w-[1.5px] self-center" />
        <div className="w-[46%] ml-auto self-center justify-end">
            <PlaceholderVideoCard src={gettingStartedQuote} />
        </div>
    </>
);

const BookOrderStepContent: React.FC<StepContentProps & { t: (key: string) => string }> = ({ step, onNext, t }) => (
    <>
        <div className="w-[50%] xlm:w-[40%] mr-auto self-center flex flex-row">
            <div className="border rounded-[8px] border-utilityGray200 w-[40px] h-[40px] flex items-center justify-center shadow-xs mr-4 min-w-[40px] min-h-[40px]">
                <PackageCheck className="w-5 h-5 text-textSecondary min-w-[20px] min-h-[20px]" />
            </div>
            <div className="flex flex-col">
                <div className="flex flex-col">
                    <h6 className="text-grayLight900 text-[16px] leading-[24px] font-semibold mb-1 text-nowrap">{step.contentTitle}</h6>
                    <p className="text-textSecondary text-[12px] leading-[18px] font-normal max-w-[300px] line-clamp-2">{step.description}</p>
                </div>
                <div className="pt-4 flex gap-x-3">
                    <ButtonCmp className="btn-outline-primary" icon={<ArrowUpRight className="w-4 h-4" />} onClick={step.primaryAction?.onClick}>
                        {step.primaryAction?.label}
                    </ButtonCmp>
                    {onNext && (
                        <ButtonCmp className="btn_secondary_black" onClick={onNext}>
                            {t('Next')}
                        </ButtonCmp>
                    )}
                </div>
            </div>
        </div>
        <hr className="bg-utilityGray200 h-[70%] w-[1.5px] self-center" />
        <div className="w-[46%] ml-auto self-center justify-end">
            <PlaceholderVideoCard src={gettingStartedBook} />
        </div>
    </>
);

const NotificationsStepContent: React.FC<StepContentProps> = ({ step }) => {
    const { t } = useTranslation();

    return (
        <>
            <div className="w-[50%] xlm:w-[40%] mr-auto self-center flex flex-row">
                <div className="border rounded-[8px] border-utilityGray200 w-[40px] h-[40px] flex items-center justify-center shadow-xs mr-4 min-w-[40px] min-h-[40px]">
                    <NotificationText className="w-5 h-5 text-textSecondary min-w-[20px] min-h-[20px]" />
                </div>
                <div className="flex flex-col">
                    <div className="flex flex-col">
                        <h6 className="text-grayLight900 text-[16px] leading-[24px] font-semibold mb-1 text-nowrap">{step.contentTitle}</h6>
                        <p className="text-textSecondary text-[12px] leading-[18px] font-normal max-w-[300px]">{step.description}</p>
                    </div>
                    <div className="pt-4 flex gap-x-3">
                        <ButtonCmp className="btn-outline-primary" onClick={step.primaryAction?.onClick}>
                            {t('Dashboard.AccountSetupStatus.NotificationSettings')}
                        </ButtonCmp>
                    </div>
                </div>
            </div>
            <hr className="bg-utilityGray200 h-[70%] w-[1.5px] self-center" />
            <div className="w-[46%] ml-auto self-center justify-end">
                <PlaceholderVideoCard src={gettingStartedNotifications} />
            </div>
        </>
    );
};

const StepContent: React.FC<StepContentProps & { t: (key: string) => string }> = ({ step, onWatch, onNext, t, isLastStep, isNotificationsEnabled, isDisabling, onDisableNotifications }) => {
    switch (step.id) {
        case 'watch':
            return <WatchStepContent step={step} onWatch={onWatch} onNext={onNext} t={t} />;
        case 'invite':
            return <InviteStepContent step={step} onWatch={onWatch} onNext={onNext} t={t} />;
        case 'quote':
            return <QuoteStepContent step={step} onWatch={onWatch} onNext={onNext} t={t} />;
        case 'book':
            return <BookOrderStepContent step={step} onWatch={onWatch} onNext={onNext} t={t} />;
        case 'notifications':
            return (
                <NotificationsStepContent
                    step={step}
                    onWatch={onWatch}
                    isLastStep={isLastStep}
                    isNotificationsEnabled={isNotificationsEnabled}
                    isDisabling={isDisabling}
                    onDisableNotifications={onDisableNotifications}
                />
            );
        default:
            return null;
    }
};

// Maps each step ID to its corresponding backend workFlowSteps key
const STEP_WORKFLOW_KEY: Record<StepId, keyof WorkflowSteps> = {
    watch: 'WATCH_PLATFORM_OVERVIEW',
    invite: 'INVITE_TEAM_MEMBER',
    quote: 'CREATE_FIRST_QUOTE',
    book: 'BOOK_FIRST_ORDER',
    notifications: 'ENABLE_TEXT_NOTIFICATIONS',
};

const STEP_ORDER: StepId[] = ['watch', 'invite', 'quote', 'book', 'notifications'];

// No dedicated view for this step yet — hide it without deleting; flip to true once the view exists.
const SHOW_PLATFORM_OVERVIEW_STEP = false;

// Computes step statuses from workFlowSteps: done if completed, first incomplete is active, rest pending
const computeStepStatuses = (wfSteps: WorkflowSteps | null): Record<StepId, 'done' | 'active' | 'pending'> => {
    const statuses: Record<StepId, 'done' | 'active' | 'pending'> = {
        watch: 'pending',
        invite: 'pending',
        quote: 'pending',
        book: 'pending',
        notifications: 'pending',
    };
    let foundActive = false;

    for (const stepId of STEP_ORDER) {
        if (wfSteps?.[STEP_WORKFLOW_KEY[stepId]]) {
            statuses[stepId] = 'done';
        } else if (!foundActive) {
            statuses[stepId] = 'active';
            foundActive = true;
        }
    }

    return statuses;
};

interface IProps {
    onCreateQuote: () => void;
}

const AccountSetupStatus: React.FC<IProps> = ({ onCreateQuote }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const dismissedKey = 'cp_setup_dismissed';
    const [isVisible, setIsVisible] = useState(() => localStorage.getItem(dismissedKey) !== 'true');
    const [isWatchVideoModal, setIsWatchVideoModal] = useState(false);
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isDisabling, setIsDisabling] = useState(false);
    const workFlowSteps = useSelector((state: RootState) => state.auth.workFlowSteps);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);

    const notificationSettings: INotificationTypes | null = currentUser?.notificationSettings?.notificationTypes ?? null;

    // Enabled when workflow step is set OR when at least one notification type is enabled
    const isNotificationsEnabled = Boolean(workFlowSteps?.ENABLE_TEXT_NOTIFICATIONS || (notificationSettings && Object.values(notificationSettings).some((entry: any) => entry?.enabled === true)));

    // Feature gate: resolved 3-tier visibility comes from getUserDetails API → auth.portalVisibility
    const isTextNotificationFeatureVisible = useSelector((state: RootState) => (state.auth as any).portalVisibility?.textNotification !== false);

    // Warm the browser cache for every step's placeholder illustration up front, so switching steps never shows a blank card while one loads.
    useEffect(() => {
        PLACEHOLDER_IMAGES.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    const handleSaveNotificationSettings = async (types: INotificationTypes) => {
        const response = await saveNotificationSettings({ notificationTypes: types });
        const responseData = response?.data?.data ?? response?.data;

        const hasAnyEnabled = Object.values(types).some((entry: any) => entry?.enabled === true);
        dispatch(setWorkflowSteps({ ...(workFlowSteps ?? {}), ENABLE_TEXT_NOTIFICATIONS: hasAnyEnabled }));
        dispatch(
            setCurrentUser({
                ...currentUser,
                notificationSettings: responseData?.notificationSettings ?? { notificationTypes: types },
            })
        );
        WalToast.success('Notification settings saved successfully');
    };

    const handleDisableGlobalNotifications = async () => {
        setIsDisabling(true);

        try {
            const allDisabled: INotificationTypes = {
                location_updates: { enabled: false, channels: { email: false, inApp: false, sms: false } },
                status_changes: { enabled: false, channels: { email: false, inApp: false, sms: false } },
                document_uploads: { enabled: false, channels: { email: false, inApp: false, sms: false } },
                order_modifications: { enabled: false, channels: { email: false, inApp: false, sms: false } },
            };

            await saveNotificationSettings({ notificationTypes: allDisabled });

            dispatch(setWorkflowSteps({ ...(workFlowSteps ?? {}), ENABLE_TEXT_NOTIFICATIONS: false }));
            dispatch(
                setCurrentUser({
                    ...currentUser,
                    notificationSettings: { notificationTypes: allDisabled },
                })
            );
            WalToast.success('Notifications disabled successfully');
        } catch {
            WalToast.error('Failed to disable notifications');
        } finally {
            setIsDisabling(false);
        }
    };

    const steps: Step[] = useMemo(
        () => [
            {
                id: 'watch',
                contentTitle: t('Dashboard.AccountSetupStatus.WatchPlatformOverview'),
                menuTitle: t('Dashboard.AccountSetupStatus.WatchPlatformOverview'),
                description: t('Dashboard.AccountSetupStatus.WatchPlatformOverview_Desc'),
                icon: <LayoutTop className="steps-item-icon" />,
                status: 'pending' as const,
                videoUrl: 'https://www.youtube.com/embed/N6ei3iMZzaw',
                videoTitle: t('Dashboard.AccountSetupStatus.PlatformWalkthrough'),
                thumbnailGradient: 'from-[#EEB0DC] to-[#A9C3EF]',
                primaryAction: {
                    label: t('Dashboard.AccountSetupStatus.Watch'),
                    onClick: () => {},
                },
            },
            {
                id: 'invite',
                contentTitle: t('Dashboard.AccountSetupStatus.InviteFirstTeamMember'),
                menuTitle: t('Dashboard.AccountSetupStatus.InviteMember'),
                description: t('Dashboard.AccountSetupStatus.InviteFirstTeamMember_Desc'),
                icon: <User01 className="steps-item-icon" />,
                status: 'pending' as const,
                videoUrl: 'https://www.youtube.com/embed/team-collaboration',
                videoTitle: t('Dashboard.AccountSetupStatus.InviteTeamMember'),
                thumbnailGradient: 'from-[#A9EFD0] to-[#63B3ED]',
                primaryAction: {
                    label: t('Dashboard.AccountSetupStatus.InviteMember'),
                    onClick: () => navigate(ROUTES.ORGANIZATION, { state: { openInviteModal: true } }),
                },
            },
            {
                id: 'quote',
                contentTitle: t('Dashboard.AccountSetupStatus.CreateFirstCustomerQuote'),
                menuTitle: t('Dashboard.AccountSetupStatus.CreateYourFirstQuote'),
                description: t('Dashboard.AccountSetupStatus.CreateFirstCustomerQuote_Desc'),
                icon: <PackagePlus className="steps-item-icon" />,
                status: 'pending' as const,
                videoUrl: 'https://www.youtube.com/embed/quote-creation',
                videoTitle: t('Dashboard.AccountSetupStatus.CreateFirstQuote'),
                thumbnailGradient: 'from-[#F6AD55] to-[#ED64A6]',
                primaryAction: {
                    label: t('Dashboard.AccountSetupStatus.CreateQuote'),
                    onClick: onCreateQuote,
                },
            },
            {
                id: 'book',
                contentTitle: t('Dashboard.AccountSetupStatus.ReviewBookedOrder'),
                menuTitle: t('Dashboard.AccountSetupStatus.BookYourFirstOrder'),
                description: t('Dashboard.AccountSetupStatus.ReviewBookedOrder_Desc'),
                icon: <PackageCheck className="steps-item-icon" />,
                status: 'pending' as const,
                videoUrl: 'https://www.youtube.com/embed/order-booking',
                videoTitle: t('Dashboard.AccountSetupStatus.ReviewYourOrder'),
                thumbnailGradient: 'from-[#9F7AEA] to-[#4FD1C5]',
                primaryAction: {
                    label: t('Dashboard.AccountSetupStatus.MyOrders'),
                    onClick: () => navigate(ROUTES.ORDERS),
                },
            },
            {
                id: 'notifications',
                contentTitle: t('Dashboard.AccountSetupStatus.EnableTextNotifications'),
                menuTitle: t('Dashboard.AccountSetupStatus.EnableTextNotificationsForOrderUpdates'),
                description: t('Dashboard.AccountSetupStatus.EnableTextNotifications_Desc'),
                icon: <NotificationText className="steps-item-icon" />,
                status: 'pending' as const,
                videoUrl: 'https://www.youtube.com/embed/notifications-setup',
                videoTitle: t('Dashboard.AccountSetupStatus.EnableTextNotifications'),
                thumbnailGradient: 'from-[#F687B3] to-[#68D391]',
                primaryAction: {
                    label: t('Dashboard.AccountSetupStatus.EnableNotifications'),
                    onClick: () => setIsNotificationModalOpen(true),
                },
                secondaryAction: {
                    label: t('Dashboard.AccountSetupStatus.Close'),
                    onClick: () => {},
                    disabled: true,
                },
            },
        ],
        [t, navigate, onCreateQuote]
    );

    // Track step statuses — initialized from Redux workFlowSteps
    const [stepStatuses, setStepStatuses] = useState<Record<StepId, 'done' | 'active' | 'pending'>>(() => computeStepStatuses(workFlowSteps));

    // Sync stepStatuses whenever workFlowSteps changes in Redux (e.g. after API calls)
    useEffect(() => {
        setStepStatuses(computeStepStatuses(workFlowSteps));
    }, [workFlowSteps]);

    const getInitialActiveStep = (): StepId => {
        const order = STEP_ORDER.filter((id) => id !== 'watch' || SHOW_PLATFORM_OVERVIEW_STEP).filter((id) => id !== 'notifications' || isTextNotificationFeatureVisible);

        for (const stepId of order) {
            if (!workFlowSteps?.[STEP_WORKFLOW_KEY[stepId]]) {
                return stepId;
            }
        }

        return order[order.length - 1] ?? 'watch';
    };

    const [selectedStep, setSelectedStep] = useState<StepId>(getInitialActiveStep());

    // Create steps with current statuses; exclude notifications step when admin has disabled the feature
    const currentSteps = useMemo(
        () =>
            steps
                .filter((step) => step.id !== 'watch' || SHOW_PLATFORM_OVERVIEW_STEP)
                .filter((step) => step.id !== 'notifications' || isTextNotificationFeatureVisible)
                .map((step) => ({
                    ...step,
                    status: stepStatuses[step.id],
                })),
        [steps, stepStatuses, isTextNotificationFeatureVisible]
    );

    const currentStep = currentSteps.find((step) => step.id === selectedStep) || currentSteps[0];

    const handleStepClick = (stepId: StepId) => {
        setSelectedStep(stepId);

        // Update statuses immutably
        setStepStatuses((prevStatuses) => {
            const newStatuses = { ...prevStatuses };

            // Update status only for non-completed steps
            Object.keys(newStatuses).forEach((id) => {
                if (newStatuses[id as StepId] !== 'done') {
                    newStatuses[id as StepId] = id === stepId ? 'active' : 'pending';
                }
            });

            return newStatuses;
        });
    };

    const handleNext = () => {
        const stepOrder: StepId[] = currentSteps.map((s) => s.id);

        // Use functional updates to ensure we're working with the latest state
        setSelectedStep((prevSelectedStep) => {
            const currentIndex = stepOrder.indexOf(prevSelectedStep);

            // Validate that current step exists in the order and is not the last step
            if (currentIndex === -1 || currentIndex >= stepOrder.length - 1) {
                return prevSelectedStep; // Don't change if invalid or already at last step
            }

            // Get the next step ID (only one step forward)
            const nextStepId = stepOrder[currentIndex + 1];

            // Just move to the next step — do NOT mark current step as done.
            // Steps are only marked done when their primary action is completed.
            setStepStatuses((prevStatuses) => {
                const newStatuses = { ...prevStatuses };

                Object.keys(newStatuses).forEach((id) => {
                    if (newStatuses[id as StepId] !== 'done') {
                        newStatuses[id as StepId] = id === nextStepId ? 'active' : 'pending';
                    }
                });

                return newStatuses;
            });

            return nextStepId;
        });
    };

    const hasNextStep = () => {
        const stepOrder: StepId[] = currentSteps.map((s) => s.id);
        const currentIndex = stepOrder.indexOf(selectedStep);

        return currentIndex < stepOrder.length - 1;
    };

    const isLastStep = () => {
        const stepOrder: StepId[] = currentSteps.map((s) => s.id);
        const currentIndex = stepOrder.indexOf(selectedStep);

        return currentIndex === stepOrder.length - 1;
    };

    const allStepsDone = currentSteps.length > 0 && currentSteps.every((step) => step.status === 'done');

    if (!isVisible || allStepsDone) return null;

    return (
        <div className="rounded-xl relative border border-utilityGray200 bg-white mb-3">
            <div className="flex">
                <div className="py-5 pr-3 pl-5 w-[330px] xlm:w-[350px] items-center flex">
                    <ul>
                        {currentSteps.map((step) => (
                            <li
                                key={step.id}
                                className={`steps-item steps-item-${step.status} ${step.id === selectedStep ? 'steps-item-active' : ''} cursor-pointer`}
                                onClick={() => handleStepClick(step.id)}
                            >
                                <div className="steps-item-icon-main">
                                    {step.status === 'done' ? (
                                        <img src={checkMarkIcon} alt="Done" className="w-[28px] h-[28px] min-w-[28px] min-h-[28px] pl-[1px] z-[2]" />
                                    ) : (
                                        <div className="steps-item-icon-wrap">{step.icon}</div>
                                    )}
                                </div>
                                <div className="steps-item-name-wrap">{step.menuTitle}</div>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="px-3 py-4 flex flex-1">
                    <StepContent
                        step={currentStep}
                        onWatch={() => setIsWatchVideoModal(true)}
                        onNext={hasNextStep() ? handleNext : undefined}
                        t={t}
                        isLastStep={isLastStep()}
                        isNotificationsEnabled={isNotificationsEnabled}
                        isDisabling={isDisabling}
                        onDisableNotifications={handleDisableGlobalNotifications}
                    />
                </div>
            </div>

            {isWatchVideoModal && <WatchVideoModal handleClose={() => setIsWatchVideoModal(false)} videoUrl={currentStep.videoUrl} videoTitle={currentStep.videoTitle} />}

            {isNotificationModalOpen && <NotificationSettingsModal handleClose={() => setIsNotificationModalOpen(false)} existingData={notificationSettings} onSave={handleSaveNotificationSettings} />}

            <div className="absolute top-5 right-4">
                <TooltipCmp message="Close">
                    <X
                        className="w-5 h-5 text-utilityBlack min-h-5 min-w-5 cursor-pointer"
                        onClick={() => {
                            localStorage.setItem(dismissedKey, 'true');
                            setIsVisible(false);
                        }}
                    />
                </TooltipCmp>
            </div>
        </div>
    );
};

export default AccountSetupStatus;
